/**
 * Catalogue of daily quest templates.
 *
 * Each template has:
 *   id        — unique identifier (used for deduplication within a day)
 *   labelKey  — i18n key under t.quests.labels.<key>
 *   type      — drives which event increments progress (see store hooks):
 *                 'new_words'         — first time the child answers an item
 *                 'correct_first'     — answered correctly on attempt 1
 *                 'perfect_session'   — finished a session with 10/10
 *                 'mistakes_cleared'  — moved an item out of the wrongPool
 *                 'sessions_played'   — completed any session
 *   target    — count needed for completion (omitted for dynamic quests —
 *               the actual target is computed at pick time)
 *   xpReward  — XP granted when the quest completes (omitted for dynamic;
 *               computed as `target * xpRewardPerStep` at pick time)
 *   weight    — relative likelihood when picking quests for the day
 *
 *   dynamic           — boolean. When true, the quest cannot complete more
 *                       items than the child actually has available right
 *                       now. At pick time `target` is clamped to
 *                       `min(targetCap, availableCount)`. If the available
 *                       count is 0, the quest is filtered out entirely so
 *                       the child never sees an impossible task.
 *   targetCap         — upper bound for the dynamic target.
 *   xpRewardPerStep   — XP per unit of target. Lets reward scale linearly
 *                       with effort (so a 3-target dynamic quest pays less
 *                       than a 5-target one).
 *   availableContextKey — name of the field on the pick-time context object
 *                         that supplies the live "available count" for this
 *                         dynamic quest.
 *
 * Three quests are picked per day, deterministically seeded by the date so
 * a refresh during the day shows the same set.
 */

export const QUEST_POOL = [
  {
    id: 'new_words_3',
    labelKey: 'new_words_3',
    type: 'new_words',
    target: 3,
    xpReward: 20,
    weight: 5,
  },
  {
    id: 'new_words_5',
    labelKey: 'new_words_5',
    type: 'new_words',
    target: 5,
    xpReward: 35,
    weight: 4,
  },
  {
    id: 'new_words_10',
    labelKey: 'new_words_10',
    type: 'new_words',
    target: 10,
    xpReward: 70,
    weight: 2,
  },
  {
    id: 'correct_first_5',
    labelKey: 'correct_first_5',
    type: 'correct_first',
    target: 5,
    xpReward: 20,
    weight: 5,
  },
  {
    id: 'correct_first_8',
    labelKey: 'correct_first_8',
    type: 'correct_first',
    target: 8,
    xpReward: 35,
    weight: 4,
  },
  {
    id: 'correct_first_15',
    labelKey: 'correct_first_15',
    type: 'correct_first',
    target: 15,
    xpReward: 70,
    weight: 2,
  },
  {
    id: 'perfect_session_1',
    labelKey: 'perfect_session_1',
    type: 'perfect_session',
    target: 1,
    xpReward: 50,
    weight: 3,
  },
  // Mistake-clearing quest is dynamic: at pick time the target is clamped
  // to the actual number of items currently in the wrong pool (1..5).
  // If the pool is empty, the quest is excluded so the child never sees an
  // impossible "clear 5 mistakes" task on a day with no mistakes to clear.
  // Leftover wrong items beyond the cap naturally carry into future days
  // because they remain in the pool until cleared.
  {
    id: 'mistakes_cleared_dynamic',
    labelKey: 'mistakes_cleared_dynamic',
    type: 'mistakes_cleared',
    dynamic: true,
    targetCap: 5,
    xpRewardPerStep: 13, // 1→13, 3→39, 5→65 XP
    availableContextKey: 'availableWrongCount',
    weight: 4,
  },
  {
    id: 'sessions_played_1',
    labelKey: 'sessions_played_1',
    type: 'sessions_played',
    target: 1,
    xpReward: 15,
    weight: 6,
  },
  {
    id: 'sessions_played_3',
    labelKey: 'sessions_played_3',
    type: 'sessions_played',
    target: 3,
    xpReward: 45,
    weight: 3,
  },
];

export const QUESTS_PER_DAY = 3;

/**
 * The single quest that IS Friday's daily mission — it replaces the normal
 * random set on Fridays. Not part of the random `QUEST_POOL` (weight 0
 * conceptually) — `buildDailyQuestSlice` returns a slice containing only this
 * quest when:
 *
 *   1. Today is a Friday.
 *   2. The child has at least one prior exercise attempt to actually
 *      review — so we never hand out an impossible quest (a brand-new child
 *      with no history gets the normal Friday set instead).
 *
 * The quest type is `history_review`; `commitSession` fires that event
 * exactly once when a history-mode session commits.
 */
export const WEEKLY_REVIEW_QUEST = Object.freeze({
  id: 'weekly_review_mandatory',
  labelKey: 'weekly_review_mandatory',
  type: 'history_review',
  target: 1,
  xpReward: 75,
  weight: 0,
});
